import React from 'react';
import { Skeleton } from '@mui/material';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import YovoImage from '../common/YovoImage';
import {
  FEATURED_DESTINATION_EXPLORE_LABEL,
  FEATURED_DESTINATION_ICON_FONT_SIZE,
  FEATURED_DESTINATION_MOBILE_BREAKPOINT,
  FEATURED_DESTINATION_MOBILE_VISIBLE_COUNT,
  FEATURED_DESTINATION_SCROLL_GAP,
  FEATURED_DESTINATION_TABLET_BREAKPOINT,
  FEATURED_DESTINATION_TABLET_VISIBLE_COUNT,
  FEATURED_DESTINATION_VISIBLE_COUNT,
} from './homepageCarousel.constants';
import styles from './FeaturedDestinationSection.module.css';
import { releaseVerticalWheel } from './releaseVerticalWheel';

function padIndex(value) {
  return String(value).padStart(2, '0');
}

function featuredCardView(item) {
  const title = item?.cardTitle || item?.output_json?.hero?.title || item?.name || 'Destination';
  return {
    title,
    description: item?.cardDescription || item?.output_json?.hero?.description || '',
    image: item?.cardImage || item?.output_json?.hero?.image || '',
    location: item?.cardLocation || item?.output_json?.place?.country || '',
    bestTime: item?.cardBestTime || '',
  };
}

/**
 * Reusable featured-destination row. The parent owns any shared homepage fetch
 * and passes the title, description, and cards in.
 */
class FeaturedDestinationSection extends React.Component {
  constructor(props) {
    super(props);
    this.scroller = null;
    this.state = {
      start: 0,
      viewportWidth: typeof window === 'undefined' ? 1200 : window.innerWidth,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items) {
      if (this.scroller) this.scroller.scrollLeft = 0;
      if (this.state.start !== 0) this.setState({ start: 0 });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.scroller) this.scroller.removeEventListener('wheel', releaseVerticalWheel);
  }

  setScroller = (node) => {
    if (this.scroller === node) return;
    if (this.scroller) this.scroller.removeEventListener('wheel', releaseVerticalWheel);
    this.scroller = node;
    if (node) node.addEventListener('wheel', releaseVerticalWheel, { passive: false });
  };

  handleResize = () => {
    if (window.innerWidth !== this.state.viewportWidth) {
      this.setState({ viewportWidth: window.innerWidth });
    }
  };

  itemCount() {
    return Array.isArray(this.props.items) ? this.props.items.length : 0;
  }

  visibleCount() {
    const width = this.state.viewportWidth;
    if (width <= FEATURED_DESTINATION_MOBILE_BREAKPOINT) return FEATURED_DESTINATION_MOBILE_VISIBLE_COUNT;
    if (width <= FEATURED_DESTINATION_TABLET_BREAKPOINT) return FEATURED_DESTINATION_TABLET_VISIBLE_COUNT;
    return FEATURED_DESTINATION_VISIBLE_COUNT;
  }

  maxStart() {
    return Math.max(0, this.itemCount() - this.visibleCount());
  }

  cardStep() {
    const card = this.scroller?.querySelector('[data-testid="featured-destination-card"]');
    const width = card ? card.getBoundingClientRect().width || card.offsetWidth : 0;
    if (!width) return 0;
    return width + FEATURED_DESTINATION_SCROLL_GAP;
  }

  scrollByCard(direction) {
    const scroller = this.scroller;
    const step = this.cardStep();
    if (!scroller || !step) return;
    const left = direction * step;
    if (typeof scroller.scrollBy === 'function') {
      scroller.scrollBy({ left, behavior: 'smooth' });
      return;
    }
    scroller.scrollLeft += left;
  }

  handleScroll = () => {
    const step = this.cardStep();
    if (!step || !this.scroller) return;
    const start = Math.min(this.maxStart(), Math.max(0, Math.round(this.scroller.scrollLeft / step)));
    if (start !== this.state.start) this.setState({ start });
  };

  handlePrev = () => {
    this.scrollByCard(-1);
  };

  handleNext = () => {
    this.scrollByCard(1);
  };

  handleExplore = (event, item) => {
    event.stopPropagation();
    if (this.props.onExplore) this.props.onExplore(item);
  };

  renderTitleIcon() {
    const Icon = this.props.icon || WaterDropIcon;
    return (
      <Icon
        className={styles.titleIcon}
        data-testid="featured-destination-icon"
        aria-hidden="true"
        fontSize={FEATURED_DESTINATION_ICON_FONT_SIZE}
        style={this.props.iconColor ? { color: this.props.iconColor } : undefined}
      />
    );
  }

  renderCard = (item, index) => {
    const view = featuredCardView(item);
    return (
      <article
        key={`${view.title}-${index}`}
        className={styles.card}
        data-testid="featured-destination-card"
        onClick={() => this.props.onExplore && this.props.onExplore(item)}
      >
        <div className={styles.media}>
          <YovoImage src={view.image} alt={view.title} width={320} height={168} />
        </div>
        <div className={styles.body}>
          {view.location ? <div className={styles.location}>{view.location}</div> : null}
          <h3 className={styles.cardTitle}>{view.title}</h3>
          {view.description ? <p className={styles.cardDescription}>{view.description}</p> : null}
        </div>
        <div className={styles.footer}>
          {view.bestTime ? <span className={styles.bestTime}>Best: {view.bestTime}</span> : null}
          <button
            type="button"
            className={styles.explore}
            onClick={(event) => this.handleExplore(event, item)}
          >
            {FEATURED_DESTINATION_EXPLORE_LABEL}
          </button>
        </div>
      </article>
    );
  };

  renderShimmer() {
    return (
      <section
        id={this.props.sectionId}
        className={styles.section}
        data-testid="featured-destination-shimmer"
        aria-busy="true"
      >
        <div className={styles.panel}>
          <div className={styles.shimmerCopy}>
            <Skeleton variant="rounded" width="48%" height={36} />
            <Skeleton variant="rounded" width="70%" height={16} />
          </div>
          <div className={styles.scroller} ref={this.setScroller}>
            {[0, 1, 2, 3].map((slot) => (
              <div key={slot} className={styles.shimmerCard}>
                <Skeleton variant="rectangular" height={168} />
                <div className={styles.body}>
                  <Skeleton variant="rounded" width="46%" height={12} />
                  <Skeleton variant="rounded" width="68%" height={18} />
                  <Skeleton variant="rounded" width="100%" height={12} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  render() {
    const total = this.itemCount();
    if (this.props.loading || total === 0) return this.renderShimmer();

    const startLabel = padIndex(this.state.start + 1);
    const totalLabel = padIndex(total);
    const progress = ((this.state.start + 1) / total) * 100;

    return (
      <section id={this.props.sectionId} className={styles.section}>
        <div className={styles.panel}>
          <div className={styles.header}>
            <div className={styles.copy}>
              <div className={styles.titleRow}>
                {this.renderTitleIcon()}
                <h2 className={styles.title}>{this.props.title}</h2>
              </div>
              {this.props.description ? <p className={styles.description}>{this.props.description}</p> : null}
            </div>
            <div className={styles.controls}>
              <div className={styles.counter} aria-live="polite">
                <span>{startLabel}</span>
                <span className={styles.counterTrack} aria-hidden="true">
                  <span className={styles.counterFill} style={{ width: `${progress}%` }} />
                </span>
                <span>{totalLabel}</span>
              </div>
              <button
                type="button"
                className={styles.arrow}
                aria-label="Previous destinations"
                disabled={this.state.start <= 0}
                onClick={this.handlePrev}
              >
                ‹
              </button>
              <button
                type="button"
                className={styles.arrow}
                aria-label="Next destinations"
                disabled={this.state.start >= this.maxStart()}
                onClick={this.handleNext}
              >
                ›
              </button>
            </div>
          </div>
          <div
            className={styles.scroller}
            data-testid="featured-destination-scroller"
            ref={this.setScroller}
            onScroll={this.handleScroll}
          >
            {(Array.isArray(this.props.items) ? this.props.items : []).map(this.renderCard)}
          </div>
        </div>
      </section>
    );
  }
}

export default FeaturedDestinationSection;
